import { zodResolver } from '@hookform/resolvers/zod';
import { validateBr } from 'js-brasil';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as z from 'zod';

import CertificaIcon from '../../assets/imgs/certifica-icon.svg';
import CourseIcon from '../../assets/imgs/course-icon.svg';
import { Button } from '../../components/Button';
import { Loading } from '../../components/Loading';
import { RecoveryPasswordModal } from '../../components/RecoveryPasswordModal';
import { ValidationCertificationModalResult } from '../../components/ValidationCertificationModalResult';
import { CertificationResponse } from '../../types/auth/CertificationResponse';
import { sanitizeCpf } from '../../utils/sanitize-cpf';
import { useGetGlobalQuery } from '../my-certifications/myCertificationsApiSlice';
import {
  useHasPlayerMutation,
  useLoginMutation,
  useValidateCertificationMutation,
} from './authApiSlice';
import { logout, selectUser, setCredentials } from './authSlice';

const SignInSchema = z.object({
  username: z.string().min(1, 'Usuário obrigatório'),
  password: z.string().min(1, { message: 'Senha obrigatória' }),
});

const ValidateCertificationSchema = z.object({
  cpf: z.custom(cpf => validateBr.cpf(cpf), {
    message: 'CPF inválido.',
  }),
});

interface isPlayer {
  value: string;
  label: string;
  openId: boolean;
}

interface HasPlayerResponse {
  data: isPlayer[];
}
type SignInForm = z.infer<typeof SignInSchema>;
type ValidateCertificationForm = z.infer<typeof ValidateCertificationSchema>;

export function SignIn() {
  const [searchParams] = useSearchParams();
  const [login] = useLoginMutation();
  const [hasPlayer] = useHasPlayerMutation();
  const [searchLoading, setSearchLoading] = useState(false);
  const [validateCertification] = useValidateCertificationMutation();
  const [step, setStep] = useState(0);
  const [selectedInstitution, setSelectedInstitution] =
    useState<isPlayer | null>(null);

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [showModal, setShowModal] = useState(false);
  const [certificationResponse, setCertificationResponse] =
    useState<CertificationResponse[]>();
  const [showModalRecoveryPassword, setShowModalRecoveryPassword] =
    useState(false);
  const { data: globalData, isLoading } = useGetGlobalQuery(
    { apiKey: undefined },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );
  const [enteredUsername, setEnteredUsername] = useState('');
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [institutionOptions, setInstitutionOptions] = useState<isPlayer[]>([]);

  const cpfForSearch = searchParams.get('cpf');

  const location = useLocation();
  const navigate = useNavigate();
  const redirectUrl = searchParams.get('updateProfile')
    ? '/profile'
    : '/my-certifications';

  const urlCertifica = process.env.REACT_APP_CERTIFICA_SITE_URL;
  const urlCourse = process.env.REACT_APP_COURSE_SITE_URL;
  const urlSite = process.env.REACT_APP_SITE_URL;

  useEffect(() => {
    if (user) {
      navigate(redirectUrl);
    }
  }, [user, navigate, location, redirectUrl]);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
  } = useForm<SignInForm>({
    mode: 'all',
    resolver: zodResolver(SignInSchema),
  });

  const {
    register: registerValidateCertification,
    handleSubmit: handleSubmitValidateCertification,
    control,
    formState: {
      isSubmitting: isSubmittingValidationCertification,
      isValid: isValidValidationCertification,
      errors: errorsValidationCertification,
    },
  } = useForm<ValidateCertificationForm>({
    mode: 'all',
    resolver: zodResolver(ValidateCertificationSchema),
  });

  const showErrorToast = (msg: string) => {
    toast.error(msg, {
      position: 'top-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const verifyIsPlayer = async (username: string) => {
    try {
      const isValidCpf = validateBr.cpf(username);

      if (!isValidCpf) {
        throw new Error();
      }

      username = sanitizeCpf(username);
      const payerFound = (await hasPlayer({ username })) as HasPlayerResponse;
      const hasOpenId = payerFound.data.some(player => player.openId);

      if (hasOpenId) {
        navigate('/sso-callback');
      } else if (payerFound.data.length === 1) {
        setStep(step + 1);
        setSelectedInstitution(payerFound.data[0]);
      } else if (payerFound.data.length > 1) {
        setInstitutionOptions(payerFound.data);
        setIsLoadingModal(true);
      } else {
        showErrorToast('Usuário Inválido');
      }
    } catch (error) {
      showErrorToast('Usuário Inválido');
    }
  };

  const handleInstitutionSelect = (selectedValue: string) => {
    const selectedOption = institutionOptions.find(
      option => option.value === selectedValue,
    );
    setSelectedInstitution(selectedOption || null);
  };

  async function handleLogin({ username, password }: SignInForm) {
    try {
      if (!globalData?.loginEnable) return;

      const isValidCpf = validateBr.cpf(username);

      const parsedUsername = isValidCpf ? sanitizeCpf(username) : username;
      const data = await login({
        username: parsedUsername,
        password,
        apiKey: selectedInstitution ? selectedInstitution.value : undefined,
      }).unwrap();

      if (data?.token) {
        dispatch(setCredentials(data));

        navigate(redirectUrl, {
          state: { from: location },
          replace: true,
        });
      } else {
        showErrorToast('Usuário ou senha inválidos');

        dispatch(logout());
      }
    } catch (error) {
      showErrorToast('Usuário ou senha inválidos');
    }
  }

  const getCertificationByCpf = useCallback(
    async (cpf: string) => {
      setSearchLoading(true);

      try {
        const data = await validateCertification({
          cpf,
        }).unwrap();

        if (data?.length > 0) {
          setShowModal(true);
          setCertificationResponse(data);
        } else {
          toast.error('Candidato não encontrado', {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      } catch (error) {
        toast.error('Candidato não encontrado', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } finally {
        setSearchLoading(false);
      }
    },
    [validateCertification],
  );

  async function handleValidateCertification({
    cpf,
  }: ValidateCertificationForm) {
    getCertificationByCpf(cpf);
  }

  useEffect(() => {
    if (cpfForSearch) {
      getCertificationByCpf(cpfForSearch);
    }
  }, [cpfForSearch, getCertificationByCpf]);

  if (
    !isLoading &&
    window.location.href.includes('bra.assbancertifica.com.br') &&
    globalData?.currentBuild !== process.env.REACT_APP_VERSION
  ) {
    toast.warning(
      'A versão do sistema está desatualizada, por favor atualize a página.',
      {
        position: 'top-center',
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'version',
      },
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <div className="mt-10 md:flex flex-row justify-center py-12 px-6 lg:px-8 container mx-auto">
        {showModal && certificationResponse && (
          <ValidationCertificationModalResult
            openModal={() => setShowModal(false)}
            data={certificationResponse}
          />
        )}

        <RecoveryPasswordModal
          show={showModalRecoveryPassword}
          cancel={() => setShowModalRecoveryPassword(false)}
        />

        <div className="md:w-[35%] md:mr-8">
          <div className="w-fit space-y-2">
            <h1 className="text-3xl font-medium text-gray-900">
              Identificação
            </h1>

            <div className="bg-[#000097] h-[3px] -ml-1 rounded-xl" />
          </div>

          <div className="mt-8 ">
            <div>
              <form
                name="signin-form"
                className="mb-0 space-y-4"
                onSubmit={handleSubmit(handleLogin)}
              >
                {step === 0 && (
                  <div>
                    <div className="mt-1">
                      <input
                        className="w-full border border-[#000097] rounded-[3px] shadow-sm focus:outline-none focus:border-blue-700 focus:ring-1 focus:ring-blue-700"
                        id="username"
                        {...register('username')}
                        placeholder="CPF"
                        type="text"
                        disabled={!globalData?.loginEnable}
                        required
                        onBlur={e => {
                          setEnteredUsername(e.currentTarget.value);
                        }}
                      />
                      {errors.username && (
                        <span className="text-red-500">
                          {errors.username.message}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Senha
                    </label>
                    <div className="mt-1">
                      <input
                        className="w-full border border-[#000097] rounded-[3px] shadow-sm focus:outline-none focus:border-blue-700 focus:ring-1 focus:ring-blue-700"
                        id="password"
                        {...register('password')}
                        placeholder="Senha"
                        type="password"
                        disabled={!globalData?.loginEnable}
                        autoComplete="current-password"
                      />
                      {errors.password && (
                        <span className="text-red-500">
                          {errors.password.message}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  {step === 0 && (
                    <div className="w-full">
                      <Button
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-sm shadow-sm text-sm font-medium text-white bg-[#000097] hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
                        onClick={() => {
                          verifyIsPlayer(enteredUsername);
                        }}
                        disabled={!globalData?.loginEnable}
                        loading={isSubmitting}
                      >
                        PRÓXIMO
                      </Button>
                    </div>
                  )}
                  {step === 1 && (
                    <div className="w-full">
                      <Button
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-sm shadow-sm text-sm font-medium text-white bg-[#000097] hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
                        disabled={
                          !globalData?.loginEnable && (isSubmitting || !isValid)
                        }
                        loading={isSubmitting}
                        type="submit"
                      >
                        {isSubmitting ? 'ENTRANDO...' : 'ENTRAR'}
                      </Button>
                    </div>
                  )}
                </div>
              </form>

              <div className="w-full flex justify-end mt-4">
                <button
                  disabled={!globalData?.loginEnable}
                  type="button"
                  onClick={() => setShowModalRecoveryPassword(true)}
                  className="border-none bg-transparent underline text-black font-normal text-sm"
                >
                  Esqueci minha senha
                </button>
              </div>

              <div>
                <p className="leading-tight text-sm mt-3">
                  Este ambiente é destinado à realização do exame de
                  certificação para pessoas que compraram uma Certificação ou um
                  Curso preparatório no site{' '}
                  <a href={urlSite} target="_blank" rel="noopener noreferrer">
                    <strong className="text-[#000097] underline">
                      ASSBAN Certifica
                    </strong>
                  </a>
                  .
                </p>
              </div>
            </div>

            <div>
              {globalData?.loginMessageEnable && globalData?.loginMessage && (
                <h1 className="text-2xl font-bold text-red-500 border-b border-solid pb-4 border-gray-300">
                  {globalData?.loginMessage}
                </h1>
              )}

              <span className="mt-4 w-full flex justify-center">
                v{process.env.REACT_APP_VERSION}
              </span>
            </div>
          </div>
        </div>

        <div className="md:w-[65%] mt-5 md:mt-0 bg-[#F8F8F9] rounded-[10px] p-16">
          <div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">
                CERTIFICAÇÃO E CURSOS ASSBAN
              </h1>
            </div>

            <p className="font-medium text-base mb-3">
              Se você precisa comprar uma certificação ou curso, vá até o site:
            </p>
          </div>

          <div className="flex space-x-3 items-center">
            <a
              href={urlCertifica}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Button className="w-full flex justify-center items-center space-x-3 py-2 px-4 h-[41px] m-0 bg-[#1075FF] hover:bg-blue-800 text-white rounded-[3px] disabled:cursor-not-allowed">
                <img src={CertificaIcon} alt="Icone de Certificação" />
                <p>CERTIFICAÇÃO PROFISSIONAL</p>
              </Button>
            </a>

            <a
              href={urlCourse}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Button className="w-full flex justify-center items-center space-x-3 py-2 px-4 h-[41px] m-0 bg-[#1075FF] hover:bg-blue-800 text-white rounded-[3px] disabled:cursor-not-allowed">
                <img src={CourseIcon} alt="Icone de Qualificação e Cursos" />
                <p>QUALIFICAÇÃO E CURSOS</p>
              </Button>
            </a>
          </div>

          <p className="mb-3 mt-8 leading-tight">
            Estar aprovado em exame de certificação é condição essencial para o
            profissional poder exercer suas atividades de forma regular. Os
            certificados emitidos pela ASSBAN-DF atestam a presença, em seus
            titulares, do conhecimento e habilidade face aos critérios técnicos
            das operações, regulamentação aplicável, o Código de Defesa do
            Consumidor, ética, ouvidoria e demais aspectos inerentes às
            atividades de relacionamento dos correspondentes com seus clientes.
          </p>
        </div>
        {isLoadingModal && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-md shadow-md w-[95%] md:w-1/5">
              <label
                htmlFor="institution"
                className="block text-sm font-medium text-gray-700"
              >
                Selecione a instituição:
              </label>
              <b className="text-sm mt-1">
                Se a inscrição foi paga por uma empresa, selecione a empresa
                listado abaixo. Caso tenha adquirido a inscrição por conta
                própria, selecione "Internet".
              </b>

              <select
                id="institution"
                name="institution"
                className="mt-3 block w-full border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-700 focus:ring-1 focus:ring-blue-700 p-2"
                onChange={e => handleInstitutionSelect(e.target.value)}
              >
                <option value="" disabled selected>
                  Selecione a Instituição
                </option>
                {institutionOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <div className="py-3 sm:flex sm:flex-row-reverse disabled:cursor-not-allowed">
                <Button
                  onClick={() => {
                    setIsLoadingModal(false);
                    setStep(step + 1);
                  }}
                  type="submit"
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:cursor-not-allowed"
                >
                  Continuar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#000097] w-full flex justify-center items-center p-10 mt-auto">
        <div className="w-full">
          <p className="text-white font-bold text-base text-center">
            Consulte aqui se você já possui certificado emitido pela ASSBAN:
          </p>

          <div className="w-[50%] mx-auto mt-8">
            <form
              name="validate-certification-form"
              className="mb-0  flex items-center"
              onSubmit={handleSubmitValidateCertification(
                handleValidateCertification,
              )}
            >
              <div className="w-full">
                <div className="flex items-center space-x-2">
                  <Controller
                    name="cpf"
                    render={({ field }) => (
                      <InputMask
                        {...field}
                        {...registerValidateCertification('cpf')}
                        mask="999.999.999-99"
                        placeholder="Digite o CPF"
                        className="w-full border border-gray-300 rounded-sm shadow-sm focus:outline-none focus:border-blue-700 focus:ring-1 focus:ring-blue-700 p-2"
                      />
                    )}
                    control={control}
                  />
                  <div className="w-[35%] h-full">
                    <Button
                      className="w-full py-2 px-4 h-[41px] m-0 bg-[#1075FF] hover:bg-blue-800 text-white rounded-sm disabled:cursor-not-allowed"
                      disabled={
                        isSubmittingValidationCertification ||
                        !isValidValidationCertification
                      }
                      loading={
                        isSubmittingValidationCertification || searchLoading
                      }
                      type="submit"
                    >
                      {isSubmitting ? 'CONSULTANDO...' : 'CONSULTAR'}
                    </Button>
                  </div>
                </div>
                {errorsValidationCertification?.cpf?.message && (
                  <span className="text-red-500 text-left w-full">{`${errorsValidationCertification?.cpf?.message}`}</span>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
